import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import {
  AdminListCard,
  AdminListTable,
  ConfirmDeleteModal,
  PageTabs,
  saveErrorReason,
  useBatchSelection,
  withFailureReasons,
} from "@components/CatalogIndex";
import type { BatchFailure } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useQuestionnairesList, useQuestionnaireMutations } from "../api";
import {
  buildQuestionnaireBatchActions,
  type QuestionnaireTab,
} from "./batchActions";
import type { Questionnaire } from "../types";

const PAGE_SIZE = 25;

const List = (): JSX.Element => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<QuestionnaireTab>("active");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const sel = useBatchSelection([tab]);
  const { archive, restore, duplicate } = useQuestionnaireMutations();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const { rows, pageMeta, isLoading, isFetching, refetch } =
    useQuestionnairesList({
      page: { number: pageNumber, size: pageSize },
      filter: { active: tab === "active" },
    });

  const selected = useMemo(
    () => rows.filter((r) => sel.selectedIds.includes(r.id)),
    [rows, sel.selectedIds]
  );

  const columns: ColumnDef<Questionnaire>[] = [
    {
      accessorKey: "name",
      header: "Survey",
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: "published",
      header: "Published?",
      cell: ({ row }) =>
        row.original.published ? <FontAwesomeIcon icon={faCheck} /> : "",
    },
    {
      accessorKey: "tagList",
      header: "Tags",
      cell: ({ row }) => row.original.tagList.join(", "),
    },
  ];

  const runOnSelected = async (
    fn: (q: Questionnaire) => Promise<unknown>,
    successText: (n: number) => string
  ) => {
    const targets = [...selected];
    const failures: BatchFailure[] = [];
    for (const q of targets) {
      try {
        await fn(q);
      } catch (err) {
        failures.push({ label: q.name, reason: saveErrorReason(err) });
      }
    }
    setToast(
      failures.length === 0
        ? { variant: "success", text: successText(targets.length) }
        : {
            variant: "danger",
            text: withFailureReasons(
              `Failed on ${failures.length} of ${targets.length} questionnaire(s).`,
              failures
            ),
          }
    );
    sel.clear();
    refetch();
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    if (tab === "active") {
      await runOnSelected(
        (q) => archive(q.id),
        (n) => `${n} questionnaire(s) archived.`
      );
    } else {
      await runOnSelected(
        (q) => restore(q.id, q.name),
        (n) => `${n} questionnaire(s) restored.`
      );
    }
  };

  const handleDuplicate = () =>
    runOnSelected(
      (q) => duplicate(q.id),
      (n) => `${n} questionnaire(s) duplicated.`
    );

  const batchActions = buildQuestionnaireBatchActions(
    tab,
    sel.selectedIds.length,
    {
      onDelete: () => setConfirmOpen(true),
      onRestore: () => setConfirmOpen(true),
      onDuplicate: handleDuplicate,
    }
  );

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminListCard
        title="Questionnaires"
        onNew={() => navigate("new")}
        newButtonLabel="+ Add A Questionnaire"
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
      >
        <PageTabs
          activeKey={tab}
          onSelect={(key) => {
            setTab(key as QuestionnaireTab);
            setPageNumber(1);
            sel.clear();
          }}
          tabs={[
            {
              key: "active",
              label: "Active",
              count: tab === "active" ? pageMeta.recordCount : undefined,
            },
            {
              key: "archived",
              label: "Archived",
              count: tab === "archived" ? pageMeta.recordCount : undefined,
            },
          ]}
        />
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          onEdit={(id) => navigate(`${id}/edit`)}
          emptyMessage="No questionnaires found."
          paginationEnabled
          pageSizeEnabled
          totalPages={pageMeta.pageCount}
          totalRows={pageMeta.recordCount}
          currentPage={pageNumber}
          setCurrentPage={(p) => {
            sel.clear();
            setPageNumber(p);
          }}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            sel.clear();
            setPageSize(size);
            setPageNumber(1);
          }}
        />
      </AdminListCard>

      <ConfirmDeleteModal
        open={confirmOpen}
        title={
          tab === "active"
            ? "Archive questionnaires?"
            : "Restore questionnaires?"
        }
        message={`Are you sure you want to ${
          tab === "active" ? "archive" : "restore"
        } ${sel.selectedIds.length} questionnaire(s)?`}
        confirmLabel={tab === "active" ? "Archive" : "Restore"}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default List;
